
import { ReactNode, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";

interface StepContainerProps {
  title: string;
  description?: string;
  children: ReactNode;
  onBack?: () => void;
  onNext?: () => void;
  isNextDisabled?: boolean;
  isBackDisabled?: boolean;
  nextButtonText?: string;
  isLastStep?: boolean;
  aiAssistantButton?: ReactNode;
}

const StepContainer = ({
  title,
  description,
  children,
  onBack,
  onNext,
  isNextDisabled = false,
  isBackDisabled = false,
  nextButtonText,
  isLastStep = false,
  aiAssistantButton,
}: StepContainerProps) => {
  const { t, language } = useLanguage();
  
  // Listen for custom submit events
  useEffect(() => {
    const handleCustomSubmit = () => {
      console.log("StepContainer: Received custom submit event");
      if (onNext && !isNextDisabled && isLastStep) {
        console.log("StepContainer: Triggering onNext from custom event");
        onNext();
      }
    };
    
    document.addEventListener('campaign-submit-clicked', handleCustomSubmit);
    return () => {
      document.removeEventListener('campaign-submit-clicked', handleCustomSubmit);
    };
  }, [onNext, isNextDisabled, isLastStep]);

  // Direct click handler with explicit console logging
  const handleNextClick = (e: React.MouseEvent) => {
    console.log("StepContainer: Next/Submit button clicked directly", { 
      isDisabled: isNextDisabled, 
      isLastStep 
    });
    
    // Always prevent default to avoid form submissions
    e.preventDefault();
    
    // Always stop propagation to prevent double handling
    e.stopPropagation();
    
    if (onNext && !isNextDisabled) {
      console.log("StepContainer: Calling onNext handler directly");
      onNext();
    } else {
      console.log("StepContainer: Next handler not called - disabled or not provided", { 
        isDisabled: isNextDisabled, 
        hasHandler: !!onNext 
      });
    }
  };

  const handleBackClick = (e: React.MouseEvent) => {
    console.log("StepContainer: Back button clicked");
    
    // Prevent default and stop propagation
    e.preventDefault();
    e.stopPropagation();
    
    if (onBack && !isBackDisabled) {
      console.log("StepContainer: Calling onBack handler");
      onBack();
    } else {
      console.log("StepContainer: Back handler not called - disabled or not provided", { 
        isDisabled: isBackDisabled, 
        hasHandler: !!onBack 
      });
    }
  };

  return (
    <div className="w-full space-y-6 sm:space-y-8 animate-fade-in">
    <div className="w-full flex items-start justify-between gap-3">
      <div className="space-y-2 max-w-3xl min-w-0">
        <h2 className="text-xl sm:text-2xl font-bold tracking-tight relative inline-block">
          {title}
          <span className="absolute -bottom-1 left-0 w-1/3 h-1 bg-primary rounded-full"></span>
        </h2>
        {description && <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">{description}</p>}
      </div>
      <div className="shrink-0">
        {aiAssistantButton}
      </div>
    </div>

      <div className="space-y-6 sm:space-y-8 bg-card dark:bg-card p-4 sm:p-8 rounded-xl sm:rounded-2xl shadow-lg border border-border backdrop-blur-sm">
        {children}
      </div>

      <div className="mobile-action-bar flex justify-between gap-3 pt-6">
        <Button
          type="button"
          variant="outline"
          onClick={handleBackClick}
          disabled={isBackDisabled}
          className="group transition-all hover:border-primary shadow-sm flex-1 sm:flex-none h-11 sm:h-10"
          data-testid="step-back-button"
        >
          {language === 'ar' ? (
            <ArrowRight className="mr-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
          ) : (
            <ArrowLeft className="mr-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
          )}
          {t("back")}
        </Button>

        <Button
          type="button"
          onClick={handleNextClick}
          disabled={isNextDisabled}
          className={`relative overflow-hidden bg-primary hover:bg-primary/90 transform transition-transform sm:hover:scale-105 shadow-md hover:shadow-lg flex-1 sm:flex-none h-11 sm:h-10 ${isNextDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          data-testid={isLastStep ? "step-submit-button" : "step-next-button"}
        >
          {isLastStep ? (
            <>
              {nextButtonText || t("submitCampaign")}
              <Check className="ml-2 h-4 w-4" />
            </>
          ) : (
            <>
              {nextButtonText || t("next")}
              {language === 'ar' ? (
                <ArrowLeft className="ml-2 h-4 w-4 group-hover:-translate-x-1 transition-transform" />
              ) : (
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              )}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default StepContainer;
